/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2022 (original work) Open Assessment Technologies SA ;
 */

import { get, writable } from 'svelte/store';
import { locale } from '../locales/i18n';
import { getDefaultTranslations, DEFAULT_LOCALE } from '../helpers/testHelper';
import { fetchConfiguration } from '../services/configuration.service.js';
import apiConfig from '../core/apiConfig.js';
import _ from 'lodash';

const DEFAULT_CONFIGS = {
    browserCompatibility: {
        accepted: {
            chrome: '>=102',
            edge: '>=102',
            firefox: '>=101',
            safari: '>=15.4',
            'mobile safari': '>=15.4'
        }
    },
    connectionCompatibility: {
        download: {
            serverUrl: apiConfig.API_URL,
            acceptable: 4194304,
            measures: [
                {
                    size: 1000,
                    timeout: 10000,
                    thread: 4
                }
            ]
        },
        upload: {
            serverUrl: apiConfig.API_URL,
            acceptable: 1258291,
            measures: [
                {
                    size: 100,
                    timeout: 10000,
                    thread: 4
                }
            ]
        },
        latency: {
            serverUrl: apiConfig.API_URL,
            acceptable: 800,
            measures: 5
        },
        jitter: {
            serverUrl: apiConfig.API_URL,
            measures: 5
        },
        concurrentUsers: 50
    },
    audioCompatibility: {
        download: {
            serverUrl: apiConfig.API_URL,
            acceptable: 4194304,
            measures: [
                {
                    size: 1000,
                    timeout: 10000,
                    thread: 4
                }
            ]
        },
        upload: {
            serverUrl: apiConfig.API_URL,
            acceptable: 1258291,
            measures: [
                {
                    size: 100,
                    timeout: 10000,
                    thread: 4
                }
            ]
        },
        latency: {
            serverUrl: apiConfig.API_URL,
            acceptable: 800,
            measures: 5
        },
        jitter: {
            serverUrl: apiConfig.API_URL,
            measures: 5
        },
        concurrentUsers: 50
    },
    webSocketConnection: {
        url: apiConfig.WEBSOCKET_URL,
        timeout: 40000,
    },
    endpointsReachability: {
        serviceEndpoints: {},
        cdnEndpoints: {},
        timeout: 10000
    },
    audio: {
        enabled: true
    },
    fields: {
        additionalData: [],
    },
};

function createConfigurationStore() {
    const configuration = writable(DEFAULT_CONFIGS);
    const { set, update, subscribe } = configuration;

    return {
        get() {
            return get(configuration);
        },
        set,
        update,
        subscribe,

        /**
         * @param {{}} partialConfig
         */
        merge(partialConfig) {
            this.update(config => (_.merge(
                config,
                partialConfig,
            )));
        },

        async load() {
            let response;

            try {
                let configResponse = await fetchConfiguration(locale);
                response = await configResponse.json();

                if (!response[locale] || _.isEmpty(response[locale])) {
                    configResponse = await fetchConfiguration(DEFAULT_LOCALE);
                    response = await configResponse.json();
                }
            } catch (err) {
                /* eslint-disable-next-line */
                console.error(`Error fetching ${locale} locale config: ${err}`);
                if (process.env.NODE_ENV === 'development') {
                    /* eslint-disable-next-line */
                    console.info(`Serving fallback translations locally`);
                    response = getDefaultTranslations();
                }
            } finally {
                this.merge(response);
            }
        }
    };
}

export default createConfigurationStore();
